import { GlobalSearch } from './nav/GlobalSearch'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  UserRound,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Menu,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { cn } from '../lib/shadcn/utils'
import { useManager } from '../hooks/useManager'
// PORT ADDITION, 22 August 2026. Owner Statements is finance-only, so the nav
// item is hidden from staff who cannot open it. The Worker refuses those
// requests regardless; this only stops the menu offering a door that is locked.
import { useCapabilities } from '../hooks/useCapabilities'
import { useGetConnectStatus, useGetInboxAccess } from '../hooks/backend/whatsapp'
import { useGetAccess as useGetTicketInboxAccess } from '../hooks/backend/inbox'
import { useGetFinesAccess } from '../hooks/backend/fines'
import { useGetMyWorkCounts } from '../hooks/backend/notifications'
import { useGetCashPlanAccess } from '../hooks/backend/banking'
import { Sheet, SheetContent, SheetTrigger } from '../lib/shadcn/sheet'
// PORT ADDITION. Retool owned the session, so its sidebar had no sign-out and no
// way to see which account you were actually using. Both matter now that we own
// authentication.
import { useSignedInEmail, signOut } from '../auth/SignedInUser'
// The grouping itself, and the path-to-group rules, live in a plain .ts module
// so Node's test runner can check them without a DOM. See navModel.ts for why
// each page sits in the groups it does.
// Reachable from every page, deliberately. See the comment in the component:
// what it replaces is a WhatsApp message sent from wherever the person was
// standing, and a form you have to navigate to first loses to WhatsApp.
import { RequestDesignButton } from './marketing/RequestDesignButton'
import { PageHelpButton } from './discipleship/PageHelpButton'
import { NotificationsButton } from './NotificationsButton'
// Beside it, and for a sharper version of the same reason: the one fact that
// makes a bug report useful is which screen it happened on, and that is the fact
// that goes stale first. A form you have to navigate to loses it.
import { ReportProblemButton } from './platform/ReportProblemButton'
import {
  PAGES,
  GROUPS,
  TOP_PAGES,
  RAIL_PAGES,
  resolveOpenGroup,
  groupIdsForPath,
  type Page,
  type PageKey,
} from './nav/navModel'

// Menu size cycles expanded -> rail -> hidden -> expanded, persisted across
// visits so the choice sticks. Which groups are open persists the same way.
type SidebarMode = 'expanded' | 'rail' | 'hidden'
const SIDEBAR_MODE_KEY = 'fleetly.sidebarMode'

function readSidebarMode(): SidebarMode {
  try {
    const raw = localStorage.getItem(SIDEBAR_MODE_KEY)
    if (raw === 'expanded' || raw === 'rail' || raw === 'hidden') return raw
  } catch {
    // ignore, fall through to default
  }
  return 'expanded'
}

function nextMode(mode: SidebarMode): SidebarMode {
  if (mode === 'expanded') return 'rail'
  if (mode === 'rail') return 'hidden'
  return 'expanded'
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src="/fleetly-mark.svg"
        alt=""
        aria-hidden="true"
        width={36}
        height={36}
        className="h-9 w-9 shrink-0"
      />
      {!compact && (
        <div className="leading-tight">
          <h1 className="text-base font-semibold text-foreground">Fleetly</h1>
          <p className="text-[11px] font-medium uppercase tracking-wide text-[hsl(var(--brand))]">
            Driving Change
          </p>
        </div>
      )}
    </div>
  )
}

// The count bubble in the top right corner of a menu item: how many things on
// that page are waiting for the signed-in person (my_work_counts_v1, 9300).
// Nothing is drawn for zero, and nothing when the count could not be read.
function CountBubble({ n, className }: { n: number | undefined; className?: string }) {
  if (!n) return null
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-none text-white tabular-nums',
        className,
      )}
    >
      {n > 99 ? '99+' : n}
    </span>
  )
}

function PageLink({
  page,
  onNavigate,
  compact = false,
  nested = false,
  count,
}: {
  page: Page
  onNavigate?: () => void
  compact?: boolean
  nested?: boolean
  count?: number
}) {
  const Icon = page.icon
  const label = count ? `${page.label}, ${count} waiting for you` : page.label
  return (
    <NavLink
      to={page.to}
      onClick={onNavigate}
      title={compact ? label : undefined}
      aria-label={label}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors sm:py-2',
          compact && 'justify-center px-2',
          nested && !compact && 'pl-9',
          isActive
            ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!compact && page.label}
      <CountBubble n={count} className="absolute right-1 top-0.5" />
    </NavLink>
  )
}

function NavItems({ onNavigate, compact = false }: { onNavigate?: () => void; compact?: boolean }) {
  const { canViewFinance, canPushXero } = useCapabilities()
  const { pathname } = useLocation()
  // One small read, cached for 15 seconds by useBackend, so moving between
  // pages does not ask again each time. A failed answer hides the item, which
  // is the safe direction: the page itself says why when it is opened.
  const inboxAccess = useGetInboxAccess<{ allowed: boolean }>()
  const loadInboxAccess = inboxAccess.trigger
  useEffect(() => {
    void loadInboxAccess({}).catch(() => undefined)
  }, [loadInboxAccess])
  const canUseWhatsappInbox = inboxAccess.data?.allowed === true
  const connectAccess = useGetConnectStatus<{ allowed: boolean }>()
  const loadConnectAccess = connectAccess.trigger
  useEffect(() => {
    void loadConnectAccess({}).catch(() => undefined)
  }, [loadConnectAccess])
  const canConnectWhatsapp = connectAccess.data?.allowed === true
  const ticketInboxAccess = useGetTicketInboxAccess<{ allowed: boolean }>()
  const loadTicketInboxAccess = ticketInboxAccess.trigger
  useEffect(() => {
    void loadTicketInboxAccess({}).catch(() => undefined)
  }, [loadTicketInboxAccess])
  const canUseInbox = ticketInboxAccess.data?.allowed === true
  const finesAccess = useGetFinesAccess<{ allowed: boolean }>()
  const loadFinesAccess = finesAccess.trigger
  useEffect(() => {
    void loadFinesAccess({}).catch(() => undefined)
  }, [loadFinesAccess])
  const canManageFines = finesAccess.data?.allowed === true
  // Asked only of finance holders: everyone else would get the banking/ 403 from
  // routePolicy, and the answer for them is no anyway.
  const cashPlanAccess = useGetCashPlanAccess<{ allowed: boolean }>()
  const loadCashPlanAccess = cashPlanAccess.trigger
  useEffect(() => {
    if (canViewFinance) void loadCashPlanAccess({}).catch(() => undefined)
  }, [canViewFinance, loadCashPlanAccess])
  const canUseCashPlan = canViewFinance && cashPlanAccess.data?.allowed === true
  // Asked again on every page change, skipping the cache, so a bubble goes down
  // as soon as you approve something and move on. A failed read leaves no
  // bubbles rather than wrong ones.
  const workCounts = useGetMyWorkCounts<{ counts: Record<string, number> }>()
  const loadWorkCounts = workCounts.trigger
  useEffect(() => {
    void loadWorkCounts({}, { skipCache: true }).catch(() => undefined)
  }, [loadWorkCounts, pathname])
  const counts = workCounts.data?.counts ?? {}
  const countFor = (key: PageKey) => counts[PAGES[key].to]

  // Exactly one group is open at a time, and the menu opens with only the group
  // holding the current page expanded. Nothing is persisted: a remembered set of
  // open groups meant arriving at a menu someone else's session had left open.
  //
  // The earlier version derived "open" as (clicked OR holds the current page),
  // which made the group you were standing in impossible to collapse: the click
  // toggled the state and the derivation immediately overrode it. State is now
  // the single source of truth and the effect below only nudges it on
  // navigation, so collapsing always takes effect on the click.
  const [openGroup, setOpenGroup] = useState<string | null>(() =>
    resolveOpenGroup(pathname, null),
  )

  useEffect(() => {
    setOpenGroup((current) => resolveOpenGroup(pathname, current))
  }, [pathname])

  const visible = (key: PageKey) => {
    const page: Page = PAGES[key]
    if (page.requiresWhatsappInbox && !canUseWhatsappInbox) return false
    if (page.requiresWhatsappConnect && !canConnectWhatsapp) return false
    if (page.requiresInbox && !canUseInbox) return false
    if (page.requiresFines && !canManageFines) return false
    if (page.requiresCashPlan && !canUseCashPlan) return false
    if (page.requiresPushXero && !canPushXero) return false
    return !page.requiresFinance || canViewFinance
  }

  const holders = groupIdsForPath(pathname)

  if (compact) {
    return (
      <nav className="flex-1 space-y-1 p-3">
        {RAIL_PAGES.filter(visible).map((key) => (
          <PageLink key={key} page={PAGES[key]} onNavigate={onNavigate} compact count={countFor(key)} />
        ))}
        <div className="space-y-1 pt-2">
          <RequestDesignButton compact />
          <ReportProblemButton compact />
        </div>
      </nav>
    )
  }

  return (
    <nav className="flex-1 space-y-1 overflow-y-auto p-3">
      {TOP_PAGES.filter(visible).map((key) => (
        <PageLink key={key} page={PAGES[key]} onNavigate={onNavigate} count={countFor(key)} />
      ))}

      {/* Above the groups, under Today. Two actions, not destinations, so not
          in navModel: neither is a route, and a path-to-group rule would have
          nothing to resolve. */}
      <div className="space-y-1 py-1">
        <RequestDesignButton />
        <ReportProblemButton />
      </div>

      {GROUPS.map((g) => {
        const pages = g.pages.filter(visible)
        // A group whose every page is gated away shows nothing rather than an
        // empty heading.
        if (pages.length === 0) return null

        const GroupIcon = g.icon
        const holdsActive = holders.includes(g.id)
        const isOpen = openGroup === g.id
        // A closed group carries the sum of its pages' bubbles, so work waiting
        // in a group you have not opened is still seen.
        const waiting = pages.reduce((sum, key) => sum + (countFor(key) ?? 0), 0)

        return (
          <div key={g.id} className="pt-1">
            <button
              type="button"
              onClick={() => setOpenGroup((current) => (current === g.id ? null : g.id))}
              aria-expanded={isOpen}
              aria-controls={`nav-group-${g.id}`}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors',
                holdsActive
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <GroupIcon className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{g.label}</span>
              {!isOpen && <CountBubble n={waiting} />}
              {/* A collapsed group holding the page you are on still says so, so
                  collapsing never loses your place. */}
              {holdsActive && !isOpen && (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--brand))]"
                />
              )}
              <span className="text-[11px] font-normal tabular-nums opacity-60">
                {pages.length}
              </span>
              {isOpen ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
              )}
            </button>
            {isOpen && (
              <div id={`nav-group-${g.id}`} className="mt-1 space-y-1">
                {pages.map((key) => (
                  <PageLink
                    key={`${g.id}:${key}`}
                    page={PAGES[key]}
                    onNavigate={onNavigate}
                    nested
                    count={countFor(key)}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

function ManagerFooter({ compact = false }: { compact?: boolean }) {
  const { name, linked, loading } = useManager()
  // PORT ADDITION: the Google account the browser actually holds. When this
  // disagrees with the manager name resolved from public.people, actions would
  // be attributed to the wrong person, so it is shown rather than hidden.
  const email = useSignedInEmail()
  if (compact) {
    return (
      <div className="flex flex-col items-center gap-2 border-t border-border p-3">
        {loading ? (
          <div className="h-8 w-8 rounded-full bg-muted" />
        ) : linked ? (
          <div
            title={name ?? undefined}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]"
          >
            <UserRound className="h-4 w-4" />
          </div>
        ) : (
          <div title="Login not linked to a manager" className="text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
          </div>
        )}
        {/* PORT ADDITION */}
        <button
          type="button"
          onClick={() => void signOut()}
          title={email ? `Sign out ${email}` : 'Sign out'}
          aria-label="Sign out"
          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    )
  }
  return (
    <div className="border-t border-border p-3">
      {loading ? (
        <div className="px-1 text-xs text-muted-foreground">Resolving manager…</div>
      ) : linked ? (
        <div className="flex items-center gap-2 px-1">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <UserRound className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{name}</p>
            {/* PORT ADDITION: was the literal string "Signed in". */}
            <p className="truncate text-[11px] text-muted-foreground">{email ?? 'Signed in'}</p>
          </div>
          {/* PORT ADDITION */}
          <button
            type="button"
            onClick={() => void signOut()}
            title="Sign out"
            aria-label="Sign out"
            className="shrink-0 rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-2 text-[11px] text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Login not linked to a manager
              {email ? <span className="block truncate opacity-80">{email}</span> : null}
            </span>
          </div>
          {/* PORT ADDITION */}
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}

// Desktop: fixed left column that cycles expanded, narrow rail, and hidden.
// A slim control strip stays visible in every mode so the menu can always be
// brought back. The choice persists across visits.
export function Sidebar() {
  const [mode, setMode] = useState<SidebarMode>(() => readSidebarMode())

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_MODE_KEY, mode)
    } catch {
      // Storage can be unavailable; not worth surfacing.
    }
  }, [mode])

  const cycle = () => setMode((m) => nextMode(m))

  return (
    <div className="hidden shrink-0 lg:flex">
      <div className="flex w-8 shrink-0 flex-col items-center border-r border-border bg-card py-4">
        <button
          type="button"
          onClick={cycle}
          title="Change menu size"
          aria-label="Change menu size"
          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {mode === 'hidden' ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </div>
      {mode !== 'hidden' && (
        <aside
          className={cn(
            'flex shrink-0 flex-col border-r border-border bg-card',
            mode === 'rail' ? 'w-16' : 'w-60',
          )}
        >
          <div
            className={cn(
              'flex items-center border-b border-border px-4 py-4',
              mode === 'rail' && 'justify-center px-2',
            )}
          >
            <BrandMark compact={mode === 'rail'} />
          </div>
          <NavItems compact={mode === 'rail'} />
          <ManagerFooter compact={mode === 'rail'} />
        </aside>
      )}
    </div>
  )
}

// The bar across the top of every page, at every size.
//
// It was mobile-only until 2 September 2026, because the desktop had the sidebar
// and needed nothing else. Notifications changed that: Luyanda asked for a button
// "at the top" and he is right that it belongs there rather than buried in a
// nav group, because a notification is the one thing you look for without having
// decided which page you are going to.
//
// On desktop the brand is left to the sidebar rather than printed twice, so the
// bar is a thin strip carrying the button and nothing else. Below lg it keeps the
// brand and the hamburger it always had, with the button between them.
export function AppTopBar() {
  const [open, setOpen] = useState(false)
  return (
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 py-2 lg:flex-nowrap lg:py-1.5">
      <div className="lg:hidden">
        <BrandMark />
      </div>
      <GlobalSearch className="order-last min-w-0 basis-full lg:order-none lg:mx-auto lg:basis-auto lg:flex-1" />
      <div className="ml-auto flex items-center gap-2 lg:ml-0">
        <PageHelpButton />
        <NotificationsButton />
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger
          aria-label="Open navigation menu"
          className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </SheetTrigger>
        <SheetContent side="left" className="flex w-72 flex-col p-0">
          <div className="flex items-center border-b border-border px-4 py-4">
            <BrandMark />
          </div>
          <NavItems onNavigate={() => setOpen(false)} />
          <ManagerFooter />
        </SheetContent>
      </Sheet>
    </header>
  )
}

// The former name. Kept as an alias rather than renamed at every call site,
// because the component is now shown at every size and the old name would be a
// lie in the one place a reader would check.
export const MobileTopBar = AppTopBar
